import React, {useState, useEffect} from 'react';
// import Notifications, {notify} from 'react-notify-toast';
import {Outlet, useParams} from 'react-router';
import {gql, useQuery} from '@apollo/client';

import API from '../../api';
import {
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Box,
  CssBaseline,
  IconButton,
  Toolbar,
  Typography,
  styled,
  Drawer,
  Divider,
  CircularProgress
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RightDrawer from 'components/RightDrawer';

const drawerWidth = 150;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open'
})<AppBarProps>(({theme, open}) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginRight: drawerWidth
  })
}));

const Main = styled('main', {shouldForwardProp: (prop) => prop !== 'open'})<{
  open?: boolean;
}>(({theme, open}) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  marginRight: -drawerWidth,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginRight: 0
  }),
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: 'relative'
}));

const DrawerHeader = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start'
}));

const gqlQuery = gql`
  query GetEvent($slug: String!) {
    getPublicEvent(slug: $slug) {
      slug
      name
      settings {
        useResults
        liveResultsCompetitionId
      }
    }
  }
`;

const EventPage = () => {
  const {slug} = useParams();

  const [open, setOpen] = React.useState(window.innerWidth >= 768);

  const [loading, setLoading] = useState(true);
  const [eventName, setEventName] = useState<string>();
  const [competitionId, setCompetitionId] = useState(0);
  const [classes, setClasses] = useState<{className: string}[]>([]);

  useQuery(gqlQuery, {
    variables: {
      slug
    },
    onCompleted: ({getPublicEvent: data}) => {
      if (data.settings.useResults) {
        setEventName(data.name);
        setCompetitionId(data.settings.liveResultsCompetitionId);
      }
    },
    onError: (error) => console.log(error)
  });

  useEffect(() => {
    if (competitionId) {
      API.fetchEvent(competitionId)
        .then(({data}) => {
          setClasses(data.classes);
        })
        .finally(() => setLoading(false));
    }
  }, [competitionId]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  if (loading) return <CircularProgress />;

  return (
    <Box sx={{display: 'flex'}}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <Typography variant="h6" noWrap sx={{flexGrow: 1}} component="div">
            {eventName}
          </Typography>
          <IconButton color="inherit" aria-label="open drawer" edge="end" onClick={handleDrawerOpen} sx={{...(open && {display: 'none'})}}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Main open={open} sx={{display: 'grid'}}>
        <DrawerHeader />
        <Outlet context={{competitionId}} />
      </Main>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth
          }
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <RightDrawer categories={classes} slug={slug} />
      </Drawer>
    </Box>
  );
};
export default EventPage;
